/**
 * PricingStep Component - Story 6.3: Create Add Product Form
 * Step 2: Pricing form (price, unit, quantity)
 */

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  pricingSchema,
  type PricingSchemaType,
} from '../../../schemas/productForm.schema'
import { PRODUCT_UNITS } from '../../../constants/productFormConstants'
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  Form,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'

export interface PricingStepProps {
  formData: Partial<PricingSchemaType>
  errors: Record<string, string>
  onChange: (data: Partial<PricingSchemaType>) => void
  isValidating?: boolean
}

/**
 * PricingStep Component
 * Form step for entering pricing information
 *
 * @component
 * @example
 * <PricingStep
 *   formData={{ price: 0, unit: '', quantity: 0 }}
 *   errors={{}}
 *   onChange={handleChange}
 * />
 */
export function PricingStep({
  formData,
  errors,
  onChange,
  isValidating = false,
}: PricingStepProps) {
  const form = useForm<PricingSchemaType>({
    resolver: zodResolver(pricingSchema),
    mode: 'onChange',
    values: {
      price: formData.price || 0,
      unit: formData.unit || '',
      quantity: formData.quantity || 0,
      minimumOrderQuantity: formData.minimumOrderQuantity || undefined,
    },
  })

  const handleChange = (data: Partial<PricingSchemaType>) => {
    onChange(data)
  }

  return (
    <Card className="pricing-step">
      <CardHeader>
        <CardTitle>Pricing</CardTitle>
        <CardDescription>
          Set the price, unit, and available quantity
        </CardDescription>
      </CardHeader>

      <CardContent>
        <Form {...form}>
          <form className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              {/* Price */}
              <FormField
                control={form.control}
                name="price"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="price">Price (INR) <span className="text-red-500">*</span></FormLabel>
                    <FormControl>
                      <Input
                        id="price"
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        {...field}
                        value={field.value || ''}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value) || 0
                          field.onChange(value)
                          handleChange({ price: value })
                        }}
                        disabled={isValidating}
                        aria-invalid={!!errors['price']}
                        aria-describedby={errors['price'] ? 'price-error' : undefined}
                      />
                    </FormControl>
                    {errors['price'] && (
                      <FormMessage id="price-error">{errors['price']}</FormMessage>
                    )}
                  </FormItem>
                )}
              />

              {/* Quantity */}
              <FormField
                control={form.control}
                name="quantity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="quantity">Quantity <span className="text-red-500">*</span></FormLabel>
                    <FormControl>
                      <Input
                        id="quantity"
                        type="number"
                        step="1"
                        min="0"
                        placeholder="0"
                        {...field}
                        value={field.value || ''}
                        onChange={(e) => {
                          const value = parseInt(e.target.value, 10) || 0
                          field.onChange(value)
                          handleChange({ quantity: value })
                        }}
                        disabled={isValidating}
                        aria-invalid={!!errors['quantity']}
                        aria-describedby={
                          errors['quantity'] ? 'quantity-error' : undefined
                        }
                      />
                    </FormControl>
                    {errors['quantity'] && (
                      <FormMessage id="quantity-error">
                        {errors['quantity']}
                      </FormMessage>
                    )}
                  </FormItem>
                )}
              />
            </div>

            {/* Minimum Order Quantity */}
            <FormField
              control={form.control}
              name="minimumOrderQuantity"
              render={({ field }) => (
                <FormItem>
                  <FormLabel htmlFor="minimumOrderQuantity">
                    Minimum Order Quantity (Optional)
                  </FormLabel>
                  <FormControl>
                    <Input
                      id="minimumOrderQuantity"
                      type="number"
                      step="1"
                      min="1"
                      placeholder="e.g., 10 (leave empty for no minimum)"
                      {...field}
                      value={field.value || ''}
                      onChange={(e) => {
                        const value = e.target.value ? parseInt(e.target.value, 10) : undefined
                        field.onChange(value)
                        handleChange({ minimumOrderQuantity: value })
                      }}
                      disabled={isValidating}
                      aria-invalid={!!errors['minimumOrderQuantity']}
                      aria-describedby={
                        errors['minimumOrderQuantity'] ? 'minimumOrderQuantity-error' : 'minimumOrderQuantity-description'
                      }
                    />
                  </FormControl>
                  <p id="minimumOrderQuantity-description" className="text-xs text-muted-foreground mt-1">
                    Set the minimum quantity customers must order. Leave empty for no minimum requirement.
                  </p>
                  {errors['minimumOrderQuantity'] && (
                    <FormMessage id="minimumOrderQuantity-error">
                      {errors['minimumOrderQuantity']}
                    </FormMessage>
                  )}
                </FormItem>
              )}
            />

            {/* Unit */}
            <FormField
              control={form.control}
              name="unit"
              render={({ field }) => (
                <FormItem>
                  <FormLabel htmlFor="unit">Unit <span className="text-red-500">*</span></FormLabel>
                  <FormControl>
                    <Select
                      value={field.value || ''}
                      onValueChange={(value) => {
                        field.onChange(value)
                        handleChange({ unit: value })
                      }}
                      disabled={isValidating}
                    >
                      <SelectTrigger
                        id="unit"
                        aria-invalid={!!errors['unit']}
                        aria-describedby={errors['unit'] ? 'unit-error' : undefined}
                      >
                        <SelectValue placeholder="Select unit" />
                      </SelectTrigger>
                      <SelectContent>
                        {PRODUCT_UNITS.map((unit) => (
                          <SelectItem key={unit} value={unit}>
                            {unit}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  {errors['unit'] && (
                    <FormMessage id="unit-error">{errors['unit']}</FormMessage>
                  )}
                </FormItem>
              )}
            />
          </form>
        </Form>
      </CardContent>
    </Card>
  )
}
